class ZipCode extends HTMLElement {
  constructor() {
    super();
    const hideSelectors = [
      '.shopify-product-form',
      '[data-product-modifications]',
      '[data-quantity-selector]',
      '[href="#bundle-customizer"]',
      '[data-section-type="product-bundle-customizer"]',
    ];
    this.hideTheseElementsSelector = hideSelectors.join(', ');
    this.hideTheseElements = this.closest('#main').querySelectorAll(this.hideTheseElementsSelector);
    this.contentDefault = this.querySelector('.form-content--default');
    this.contentReset = this.querySelector('.form-content--reset');
    this.zipCode = this.querySelector('#zip-code-input');
    this.zipCodeError = this.querySelector('#zip-code-error');
    this.distanceMessage = this.querySelector('#distance-message');
    this.closestYardName = '';
    this.variantRadiosWrapper = document.querySelector('#variant-radios-Location');
    this.variantRadiosArray = this.variantRadiosWrapper.querySelectorAll('input[type="radio"]');
    this.variantTitles = Array.from(this.variantRadiosArray).map((radio) => radio.value);

    this.buttonChange = this.contentReset.querySelector('button');

    this.parentNode.querySelector('[data-selects]').classList.add('hidden');

    // Get settings from data attributes
    this.apiKey = this.dataset.apiKey;
    this.distanceThreshold = parseFloat(this.dataset.distanceThreshold) || 500;
    this.enableDistanceDetection = this.dataset.enableDistanceDetection === 'true';

    // Coordinates for known locations
    this.locationCoordinates = {
      'Santa Maria': { lat: 34.9530, lng: -120.4357 },
      'Visalia': { lat: 36.3302, lng: -119.2921 },
      'Stockton': { lat: 37.9577, lng: -121.2908 },
      'Long Beach': { lat: 33.7701, lng: -118.1937 },
      'Oakland': { lat: 37.8044, lng: -122.2712 }
    };

    this.userLocation = null;

    document.addEventListener('DOMContentLoaded', this.onInit.bind(this));
  }

  connectedCallback() {
    this.addEventListener('submit', this.handleSubmit.bind(this));
    this.buttonChange.addEventListener('click', this.onChangeClick.bind(this));

    // Check user location on load if API key is provided and distance detection is enabled
    if (this.apiKey && this.enableDistanceDetection) {
      this.getUserLocation();
    }
  }

  // Calculate distance between two coordinates using Haversine formula
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 3959; // Radius of Earth in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  }

  toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  // Get user location from IP using ipgeolocation.io API
  async getUserLocation() {
    if (!this.apiKey) {
      return;
    }

    try {
      const response = await fetch(`https://api.ipgeolocation.io/ipgeo?apiKey=${this.apiKey}`);
      if (!response.ok) {
        throw new Error('Failed to fetch user location');
      }
      const data = await response.json();
      this.userLocation = {
        lat: parseFloat(data.latitude),
        lng: parseFloat(data.longitude)
      };

      // Check distance if location is already selected
      if (this.closestYardName) {
        this.checkDistance(this.closestYardName);
      }
    } catch (error) {
      console.error('Error fetching user location:', error);
    }
  }

  // Check distance between user and selected location
  checkDistance(locationName) {
    if (!this.enableDistanceDetection || !this.userLocation || !this.locationCoordinates[locationName]) {
      return;
    }

    const locationCoords = this.locationCoordinates[locationName];
    const distance = this.calculateDistance(
      this.userLocation.lat,
      this.userLocation.lng,
      locationCoords.lat,
      locationCoords.lng
    );

    if (distance > this.distanceThreshold) {
      this.distanceMessage.classList.remove('hidden');
    } else {
      this.distanceMessage.classList.add('hidden');
    }
  }

  triggerVariantChange(radio) {
    radio.dispatchEvent(
      new Event('change', {
        bubbles: true,
        cancelable: true,
      })
    );
  }

  onInit() {
    // First, check for auto-detected location
    const autoLocation = this.getAutoDetectedLocation();

    if (autoLocation && autoLocation.nearestYard) {
      // Use auto-detected location
      this.closestYardName = autoLocation.nearestYard.name;
      this.selectVariantByLocation(this.closestYardName);

      // Show the location in UI
      this.querySelector('.form-location--current span').innerHTML = this.closestYardName;
      this.switchContent();
      this.zipCodeError.classList.add('hidden');

      console.log('[ZipCode] Using auto-detected location:', this.closestYardName);
      return;
    }

    // Fallback to manually saved zip code
    const savedZipCode = localStorage.getItem('zipCode');
    const savedClosestYardName = localStorage.getItem('closestYardName');

    if (savedZipCode && savedClosestYardName) {
      this.zipCode.value = savedZipCode;
      this.closestYardName = savedClosestYardName;

      this.selectVariantByLocation(this.closestYardName);

      this.querySelector('.form-location--current span').innerHTML = this.closestYardName;
      this.switchContent();
      this.zipCodeError.classList.add('hidden');

      // Check distance if user location is already available
      if (this.userLocation) {
        this.checkDistance(this.closestYardName);
      }
    } else {
      this.hideTheseElements.forEach((element) => {
        if (element) {
          element.classList.add('hidden');
        }
      });
    }
  }

  getAutoDetectedLocation() {
    try {
      const location = localStorage.getItem('userLocation');
      return location ? JSON.parse(location) : null;
    } catch (error) {
      console.error('[ZipCode] Error reading auto-detected location:', error);
      return null;
    }
  }

  selectVariantByLocation(locationName) {
    this.variantRadiosArray.forEach((radio) => {
      if (radio.value === locationName) {
        radio.checked = true;
        this.triggerVariantChange(radio);
        this.variantRadiosWrapper.dispatchEvent(
          new Event('change', {
            bubbles: true,
            cancelable: true,
          })
        );
      }
    });
  }

  switchContent() {
    if (this.contentDefault.classList.contains('hidden')) {
      this.contentDefault.classList.remove('hidden');
      this.contentReset.classList.add('hidden');
      this.hideTheseElements.forEach((element) => {
        if (element) {
          element.classList.add('hidden');
        }
      });
    } else {
      this.contentDefault.classList.add('hidden');
      this.contentReset.classList.remove('hidden');
      this.hideTheseElements.forEach((element) => {
        if (element) {
          element.classList.remove('hidden');
        }
      });
    }
  }

  handleSubmit(event) {
    event.preventDefault();
    event.submitter.setAttribute('disabled', '');

    const requestData = {
      zip: this.zipCode.value,
      variants: this.variantTitles,
    };

    fetch('https://midstatecontainers-location-detection--development.gadget.app/zip', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    })
      .then((response) => {
        if (!response.ok) {
          this.zipCodeError.classList.remove('hidden');

          return Promise.reject(new Error('Invalid Zip Code'));
        }

        this.zipCodeError.classList.add('hidden');

        return response.json();
      })
      .then((data) => {
        this.closestYardName = data.closestYard.name;

        let found = false;

        this.variantRadiosArray.forEach((radio) => {
          if (radio.value === this.closestYardName) {
            radio.checked = true;
            found = true;
            this.triggerVariantChange(radio);
          }
        });

        if (found) {
          this.switchContent();
          this.querySelector('.form-location--current span').innerHTML = this.closestYardName;
          this.zipCodeError.classList.add('hidden');
          event.submitter.removeAttribute('disabled');

          localStorage.setItem('zipCode', this.zipCode.value);
          localStorage.setItem('closestYardName', this.closestYardName);

          // Check distance after location is selected
          this.checkDistance(this.closestYardName);
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        this.zipCodeError.classList.remove('hidden');
        event.submitter.removeAttribute('disabled');
      });
  }

  onChangeClick(event) {
    event.preventDefault();
    this.switchContent();
    // Hide distance message when changing location
    if (this.distanceMessage) {
      this.distanceMessage.classList.add('hidden');
    }
  }
}

customElements.define('component-zip-code', ZipCode);
